import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { CPTBRow, DashboardData } from '../../dtc-commerce-mmm-types';
import { formatFieldName } from '../../hooks/useChannelAggregation';
import {
  AttributionMetric,
  AttributionMetricType,
  FlowDirection,
  FlowDirectionType,
  FlowStepKind,
  FlowStepKindType,
  MIN_PARTNERS_TO_FOLD,
  SMALL_PARTNER_SHARE_OF_DIRECT
} from './channel-drawer-attribution-flow-constants';

export interface MetricPair {
  spend: number;
  contribution: number;
}

// Credit that moved between the feature and one partner channel, summed over the window.
export interface FlowPartner {
  key: string;
  label: string;
  spend: number;
  contribution: number;
}

export interface FlowBreakdown {
  featureName: string;
  mediaType: string;
  // Null for features outside two-stage, which have no partners.
  direction: FlowDirectionType | null;
  direct: MetricPair;
  actualized: MetricPair;
  partners: FlowPartner[];
}

export interface FlowStep {
  key: string;
  kind: FlowStepKindType;
  label: string;
  value: number;
  // The bar's extent on the chart's scale. Partner steps float between running totals.
  start: number;
  end: number;
  // Where the walk stood before this step; the dashed connector from the row above lands here.
  connectorAt: number | null;
  // The partner behind a PARTNER step, or every partner folded into an OTHERS step.
  partners: FlowPartner[];
}

// A partner or folded step before it's placed on the walk.
type FlowPiece = Pick<FlowStep, 'key' | 'kind' | 'label' | 'value' | 'partners'>;

export const metricValue = (pair: MetricPair, metric: AttributionMetricType): number =>
  metric === AttributionMetric.SPEND ? pair.spend : pair.contribution;

const directionFor = (mediaType: string | undefined): FlowDirectionType | null => {
  if (mediaType === 'demand_generation') return FlowDirection.ADDED;
  if (mediaType === 'demand_capture') return FlowDirection.REDUCED;
  return null;
};

const sumForFeature = (
  rows: CPTBRow[],
  featureName: string,
  field: 'effectiveNormalized' | 'rawSpend' | 'effectiveSpend'
): number => rows.reduce((total, r) => (r.name === featureName ? total + r[field] : total), 0);

// Direct and actualized totals for one feature, plus the partner channels its credit moved to or
// from. Both metrics are kept so a tooltip can show spend and contribution side by side.
export const computeFlowBreakdown = (data: DashboardData, featureName: string): FlowBreakdown => {
  const channelByName = new Map(data.channelHierarchy.channels.map(c => [c.name, c.channel]));
  const mediaType =
    data.channelHierarchy.channels.find(c => c.name === featureName)?.mediaType ?? '';
  const direction = directionFor(mediaType);

  const partnersByChannel = new Map<string, FlowPartner>();
  (data.twoStageDecomp.get(featureName) ?? []).forEach(entry => {
    const partnerChannel = channelByName.get(entry.secondaryName);
    if (!partnerChannel || entry.categoryType !== direction) return;
    const partner = partnersByChannel.get(partnerChannel) ?? {
      key: partnerChannel,
      label: formatFieldName(partnerChannel),
      spend: 0,
      contribution: 0
    };
    partner.spend += entry.spend;
    partner.contribution += entry.effect;
    partnersByChannel.set(partnerChannel, partner);
  });

  return {
    featureName,
    mediaType,
    direction,
    direct: {
      spend: sumForFeature(data.raw.cptbT12M, featureName, 'rawSpend'),
      contribution: sumForFeature(data.raw.cptbT12M, featureName, 'effectiveNormalized')
    },
    actualized: {
      spend: sumForFeature(data.actualized.cptbT12M, featureName, 'effectiveSpend'),
      contribution: sumForFeature(data.actualized.cptbT12M, featureName, 'effectiveNormalized')
    },
    partners: [...partnersByChannel.values()]
  };
};

// Ranks partners by the selected metric, largest first, dropping any with nothing to show. Those
// under 1% of the direct total split off to be folded together, but only when at least two qualify.
export const foldSmallPartners = (
  partners: FlowPartner[],
  metric: AttributionMetricType,
  direct: number
): { shown: FlowPartner[]; folded: FlowPartner[] } => {
  const ranked = partners
    .filter(p => metricValue(p, metric) > 0)
    .sort((a, b) => metricValue(b, metric) - metricValue(a, metric));
  const threshold = direct * SMALL_PARTNER_SHARE_OF_DIRECT;
  const small = ranked.filter(p => metricValue(p, metric) < threshold);
  if (small.length < MIN_PARTNERS_TO_FOLD) return { shown: ranked, folded: [] };
  return { shown: ranked.filter(p => metricValue(p, metric) >= threshold), folded: small };
};

// The waterfall from direct to actualized: the direct total, one step per partner (each starting
// where the previous one ended), an optional folded step, then the actualized total.
export const buildFlowSteps = (
  breakdown: FlowBreakdown,
  metric: AttributionMetricType
): FlowStep[] => {
  const direct = metricValue(breakdown.direct, metric);
  const actualized = metricValue(breakdown.actualized, metric);
  const isAdded = breakdown.direction === FlowDirection.ADDED;
  const { shown, folded } = foldSmallPartners(breakdown.partners, metric, direct);

  const pieces: FlowPiece[] = shown.map(p => ({
    key: `partner:${p.key}`,
    kind: FlowStepKind.PARTNER,
    label: p.label,
    value: metricValue(p, metric),
    partners: [p]
  }));
  if (folded.length > 0) {
    pieces.push({
      key: 'others',
      kind: FlowStepKind.OTHERS,
      label: conditionallyPluralizeWithCount(folded.length, 'other channel'),
      value: folded.reduce((total, p) => total + metricValue(p, metric), 0),
      partners: folded
    });
  }

  const steps: FlowStep[] = [
    {
      key: 'direct',
      kind: FlowStepKind.DIRECT,
      label: 'Direct',
      value: direct,
      start: 0,
      end: direct,
      connectorAt: null,
      partners: []
    }
  ];
  let running = direct;
  pieces.forEach(piece => {
    const next = isAdded ? running + piece.value : running - piece.value;
    steps.push({
      ...piece,
      start: Math.min(running, next),
      end: Math.max(running, next),
      connectorAt: running
    });
    running = next;
  });
  steps.push({
    key: 'actualized',
    kind: FlowStepKind.ACTUALIZED,
    label: 'Actualized',
    value: actualized,
    start: 0,
    end: actualized,
    connectorAt: pieces.length > 0 ? running : null,
    partners: []
  });
  return steps;
};

// What the toggled metric is called on screen: spend, or the model's own KPI.
export const metricLabel = (metric: AttributionMetricType, isRevenue: boolean): string => {
  if (metric === AttributionMetric.SPEND) return 'Spend';
  return isRevenue ? 'Revenue' : 'Conversions';
};

// Partner rows say which way the credit moved instead of signing the amount: "To Paid Shopping"
// when demand capture hands it over, "From Affiliate" when demand generation picks it up.
export const flowRowLabel = (step: FlowStep, direction: FlowDirectionType | null): string => {
  if (step.kind !== FlowStepKind.PARTNER && step.kind !== FlowStepKind.OTHERS) return step.label;
  return direction === FlowDirection.ADDED ? `From ${step.label}` : `To ${step.label}`;
};

// The middle term of the "direct ± redistribution = actualized" summary: the exact difference, so it
// matches the partner rows below it. Taking it from the rounded totals instead ($2.8M and $3.0M
// shown, so $200K) put it out of step with rows that add up to the real $251K.
export const redistributionAmount = (direct: number, actualized: number): number =>
  Math.abs(actualized - direct);
